import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../config/theme.config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faTruckFast,
  faEnvelope,
  faLock,
  faUser,
  faPhone,
  faIdCard,
  faHandshake,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { utilsService, DniTypeObject, applyDniPrefix, getMaxLengthFromMask, getDniCompositeKey, findDniTypeByCompositeKey } from "../../services/utils.service";
import { countryService, CountryFull } from "../../services/country.service";
import useForm, { ValidationRules } from "../../hooks/useForm";
import Swal from "sweetalert2";
import { formatPhoneNational, isValidPhone, getPhonePlaceholder } from "../../config/phone.utils";

import styles from "../../styles/auth/AuthPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
import logoElpaket from "../../assets/images/logo-elpaket-color.png";

const RegisterPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dniTypes, setDniTypes] = useState<DniTypeObject[]>([]);
  const [loadingDniTypes, setLoadingDniTypes] = useState(true);
  const [countries, setCountries] = useState<CountryFull[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);


  const { register, registerWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const from = (location.state as any)?.from || "/customer/dashboard";

  const validationRules: ValidationRules<any> = {
    nombre: (value) => (value ? null : "El nombre es requerido"),
    apellido: (value) => (value ? null : "El apellido es requerido"),
    dni: (value, values) => {
      if (!value) return "El documento es requerido";
      const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.dniType);
      if (selectedDniObj && selectedDniObj.mask) {
        const regex = new RegExp(selectedDniObj.mask);
        if (!regex.test(value)) {
          return `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
        }
      }
      return null;
    },
    phone: (value, values) => {
      if (!value) return "El teléfono es requerido";
      if (!isValidPhone(value, values.country)) return "Número de teléfono inválido";
      return null;
    },
    email: (value) => {
      if (!value) return "El email es requerido";
      if (!/\S+@\S+\.\S+/.test(value)) return "Email inválido";
      return null;
    },
    password: (value) => {
      if (!value) return "La contraseña es requerida";
      if (value.length < 6) return "Debe tener al menos 6 caracteres";
      if (value.length > 16) return "Debe tener máximo 16 caracteres";
      if (!/[a-z]/.test(value)) return "Debe contener al menos una minúscula";
      if (!/[A-Z]/.test(value)) return "Debe contener al menos una mayúscula";
      if (!/[0-9]/.test(value)) return "Debe contener al menos un número";
      if (!/[^A-Za-z0-9]/.test(value))
        return "Debe contener al menos un carácter especial";
      return null;
    },
    confirmPassword: (value, values) => {
      if (!value) return "Confirma tu contraseña";
      if (value !== values.password) return "Las contraseñas no coinciden";
      return null;
    },
    dniType: (value) => (value ? null : "Selecciona un tipo de documento"),
    country: (value) => (value ? null : "El país es requerido"),
  };

  const initialValues = {
    email: "",
    password: "",
    confirmPassword: "",
    nombre: "",
    apellido: "",
    phone: "",
    dni: "",
    dniType: "",
    country: "",
    razonSocial: "",
  };

  const { values, errors, handleChange, handleSubmit, setValues, setErrors, isValid } = useForm(
    initialValues,
    validationRules
  );

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const formatted = formatPhoneNational(val, values.country, selectedCountryObj?.phoneCode);
    setValues((prev: any) => ({ ...prev, phone: formatted }));

    const isVal = isValidPhone(formatted, values.country);
    setErrors((prev: any) => ({
      ...prev,
      phone: !formatted ? "El teléfono es requerido" : !isVal ? "Número de teléfono inválido" : null,
    }));
  };

  const handleDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.dniType);
    const formatted = applyDniPrefix(val, selectedDniObj?.mask);
    setValues((prev: any) => ({ ...prev, dni: formatted }));

    const error = validationRules.dni(formatted, { ...values, dni: formatted });
    setErrors((prev: any) => ({
      ...prev,
      dni: error,
    }));
  };

  const handleKindOfDniChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setValues((prev: any) => ({
      ...prev,
      dniType: val,
      dni: "",
    }));
    setErrors((prev: any) => ({
      ...prev,
      dniType: undefined,
      dni: undefined,
    }));
  };

  const selectedCountryObj = React.useMemo(
    () => countries.find((c) => c.iso2Country === values.country) ?? null,
    [countries, values.country]
  );

  const selectedDniObj = React.useMemo(
    () => findDniTypeByCompositeKey(dniTypes, values.dniType) ?? null,
    [dniTypes, values.dniType]
  );

  const showRazonSocial = selectedDniObj?.requiereRazonSocial === true;



  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const allCountries = await countryService.getAllCountries();
        setCountries(allCountries);
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchDniTypes = async () => {
      try {
        setLoadingDniTypes(true);
        const types = await utilsService.getDniTypes(values.country);
        setDniTypes(types);
        if (types.length > 0) {
          setValues((prev: any) => ({ ...prev, dniType: types[0].kindOfDNI }));
        } else {
          setValues((prev: any) => ({ ...prev, dniType: "" }));
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error al cargar los tipos de documento.",
        });
        console.error(err);
      } finally {
        setLoadingDniTypes(false);
      }
    };

    if (values.country) {
      fetchDniTypes();
    } else {
      setDniTypes([]);
      setValues((prev: any) => ({ ...prev, dniType: "" }));
      setLoadingDniTypes(false);
    }
  }, [values.country, setValues]);

  const handleRegister = async () => {
    setLoading(true);

    try {
      const registerValidation = await register(
        values.email,
        values.password,
        values.nombre,
        values.apellido,
        selectedCountryObj?.phoneCode
          ? `${selectedCountryObj.phoneCode.startsWith("+") ? "" : "+"}${selectedCountryObj.phoneCode}${values.phone}`
          : values.phone,
        values.dni,
        findDniTypeByCompositeKey(dniTypes, values.dniType)?.kindOfDNI || values.dniType,
        values.country,
        showRazonSocial ? values.razonSocial : undefined
      );
      if (registerValidation.success) {
        const toTitleCase = (str: string) =>
          str
            ? str
                .trim()
                .split(/\s+/)
                .map((w) => (w.length > 1 ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w.toUpperCase()))
                .join(" ")
            : "";
        const fullName = `${toTitleCase(values.nombre)} ${toTitleCase(values.apellido)}`.trim();
        const bodyText = fullName
          ? `${fullName}, tu cuenta ha sido creada correctamente. Revisa tu bandeja de entrada y la carpeta de spam o correo no deseado para verificar la cuenta.`
          : "Tu cuenta ha sido creada correctamente. Revisa tu bandeja de entrada y la carpeta de spam o correo no deseado para verificar la cuenta.";

        Swal.fire({
          html: `
            <div class="custom-swal-container">
              <img src="${logoElpaket}" class="custom-swal-logo" alt="elPaket" />
              <div class="custom-swal-icon-wrapper">
                <div class="spark spark-1"></div>
                <div class="spark spark-2"></div>
                <div class="spark spark-3"></div>
                <div class="spark spark-4"></div>
                <div class="custom-swal-icon">✓</div>
              </div>
              <h2 class="custom-swal-title">
                ¡Registro <span class="highlight">exitoso!</span>
              </h2>
              <p class="custom-swal-text">${bodyText}</p>
            </div>
          `,
          showConfirmButton: true,
          confirmButtonText: "Aceptar",
          buttonsStyling: false,
          customClass: {
            popup: 'custom-swal-popup-card',
            confirmButton: 'custom-swal-confirm-btn',
          },
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/login", { replace: true });
          }
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error en el registro",
          text: registerValidation.message || "Error al crear la cuenta.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ha ocurrido un error inesperado.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    if (!values.country) return;
    setLoading(true);

    registerWithGoogle(values.country)
      .then(({ success, error }) => {
        if (success) {
          Swal.fire({
            html: `
              <div class="custom-swal-container">
                <img src="${logoElpaket}" class="custom-swal-logo" alt="ElPaket Logo" />
                <div class="custom-swal-icon-wrapper">
                  <div class="spark spark-1"></div>
                  <div class="spark spark-2"></div>
                  <div class="spark spark-3"></div>
                  <div class="spark spark-4"></div>
                  <div class="custom-swal-icon">✓</div>
                </div>
                <h2 class="custom-swal-title">
                  ¡Registro <span class="highlight">Exitoso!</span>
                </h2>
                <p class="custom-swal-text">Tu cuenta ha sido creada y vinculada con Google.</p>
              </div>
            `,
            showConfirmButton: true,
            confirmButtonText: "Aceptar",
            buttonsStyling: false,
            customClass: {
              popup: 'custom-swal-popup-card',
              confirmButton: 'custom-swal-confirm-btn',
            },
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(from, { replace: true });
            }
          });
        } else if (error && !error.includes("cancelado")) {
          Swal.fire({
            icon: "error",
            title: "Error en el registro con Google",
            text: error || "Error en el registro con Google.",
          });
        }
      })
      .catch((err) => {
        console.error("Error inesperado en handleGoogleRegister:", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Ha ocurrido un error inesperado.",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className={styles.authPage}>
      <div className={`${styles.authContainer} ${styles.registerMode} ${styles.compactMode}`}>
        <div className={styles.authCard}>
          <div className={styles.authHeader}>
            <h2 className={styles.authTitle}>Crear Cuenta</h2>
            <p className={styles.authSubtitle}>Únete a nuestra plataforma de envíos</p>
          </div>

          <form onSubmit={handleSubmit(handleRegister)} noValidate>
            <div className={styles.registerFieldsGrid}>
              {/* País */}
              <div className={`${styles.formGroup} ${styles.fullWidthField}`}>
                <label htmlFor="country" className={styles.formLabel}>
                  País
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faGlobe} className={styles.inputIcon} />
                  <select
                    id="country"
                    name="country"
                    required
                    value={values.country}
                    onChange={handleChange}
                    className={`${styles.authFormInput} ${errors.country ? styles.isInvalid : ""
                      }`}
                    disabled={loadingCountries}
                  >
                    {loadingCountries ? (
                      <option value="">Cargando países...</option>
                    ) : (
                      <>
                        <option value="">Seleccionar País</option>
                        {countries.map((c) => (
                          <option key={c.id} value={c.iso2Country}>
                            {c.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                {errors.country && (
                  <p className={styles.invalidFeedback}>{errors.country}</p>
                )}
              </div>

              {/* Nombre */}
              <div className={styles.formGroup}>
                <label htmlFor="nombre" className={styles.formLabel}>
                  Nombre
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                  <input
                    id="nombre"
                    name="nombre"
                    type="text"
                    required
                    value={values.nombre}
                    onChange={handleChange}
                    className={`${styles.authFormInput} ${errors.nombre ? styles.isInvalid : ""
                      }`}
                    placeholder="Tu nombre"
                  />
                </div>
                {errors.nombre && (
                  <p className={styles.invalidFeedback}>{errors.nombre}</p>
                )}
              </div>

              {/* Apellido */}
              <div className={styles.formGroup}>
                <label htmlFor="apellido" className={styles.formLabel}>
                  Apellido
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
                  <input
                    id="apellido"
                    name="apellido"
                    type="text"
                    required
                    value={values.apellido}
                    onChange={handleChange}
                    className={`${styles.authFormInput} ${errors.apellido ? styles.isInvalid : ""
                      }`}
                    placeholder="Tu apellido"
                  />
                </div>
                {errors.apellido && (
                  <p className={styles.invalidFeedback}>{errors.apellido}</p>
                )}
              </div>

              {/* Tipo de Documento */}
              <div className={styles.formGroup}>
                <label htmlFor="dniType" className={styles.formLabel}>
                  Tipo de Documento
                </label>
                <div className={styles.inputContainer}>
                  <select
                    id="dniType"
                    name="dniType"
                    required
                    value={values.dniType}
                    onChange={handleKindOfDniChange}
                    className={`${styles.authFormInput} ${errors.dniType ? styles.isInvalid : ""
                      }`}
                    disabled={loadingDniTypes || !values.country}
                  >
                    {loadingDniTypes ? (
                      <option>Cargando...</option>
                    ) : !values.country ? (
                      <option value="">Selecciona un país primero</option>
                    ) : (
                      dniTypes.map((type) => (
                        <option key={getDniCompositeKey(type)} value={getDniCompositeKey(type)}>
                          {type.prefix || type.kindOfDNI}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                {errors.dniType && (
                  <p className={styles.invalidFeedback}>{errors.dniType}</p>
                )}
              </div>

              {/* Documento */}
              <div className={styles.formGroup}>
                <label htmlFor="dni" className={styles.formLabel}>
                  Número de Documento
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faIdCard} className={styles.inputIcon} />
                  <input
                    id="dni"
                    name="dni"
                    type="text"
                    required
                    value={values.dni}
                    onChange={handleDniChange}
                    className={`${styles.authFormInput} ${errors.dni ? styles.isInvalid : ""}`}
                    placeholder="Tu número de documento"
                    maxLength={getMaxLengthFromMask(findDniTypeByCompositeKey(dniTypes, values.dniType)?.mask) || 20}
                  />
                </div>
                {errors.dni && (
                  <p className={styles.invalidFeedback}>{errors.dni}</p>
                )}
              </div>

              {/* Razón Social (condicional) */}
              {showRazonSocial && (
                <div className={`${styles.formGroup} ${styles.fullWidthField}`}>
                  <label htmlFor="razonSocial" className={styles.formLabel}>
                    Razón Social
                  </label>
                  <div className={styles.inputContainer}>
                    <FontAwesomeIcon icon={faHandshake} className={styles.inputIcon} />
                    <input
                      id="razonSocial"
                      name="razonSocial"
                      type="text"
                      value={values.razonSocial}
                      onChange={handleChange}
                      className={styles.authFormInput}
                      placeholder="Nombre de la empresa o razón social"
                    />
                  </div>
                </div>
              )}

              {/* Teléfono */}
              <div className={`${styles.formGroup} ${styles.fullWidthField}`}>
                <label htmlFor="phone" className={styles.formLabel}>
                  Teléfono
                </label>
                <div
                  className={`${styles.phoneInputWrapper} ${errors.phone ? styles.isInvalidWrapper : ""
                    }`}
                  style={!values.country ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                >
                  {values.country && (
                    <div className={styles.phonePrefix}>
                      <img
                        src={`https://flagcdn.com/w40/${values.country.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w80/${values.country.toLowerCase()}.png 2x`}
                        width="20"
                        height="14"
                        alt={values.country}
                        className={styles.phonePrefixFlag}
                        loading="lazy"
                        decoding="async"
                      />
                      {selectedCountryObj?.phoneCode && (
                        <span className={styles.phonePrefixCode}>
                          {selectedCountryObj.phoneCode.startsWith("+")
                            ? selectedCountryObj.phoneCode
                            : `+${selectedCountryObj.phoneCode}`}
                        </span>
                      )}
                    </div>
                  )}
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    disabled={!values.country}
                    value={values.phone}
                    onChange={handlePhoneChange}
                    placeholder={!values.country ? "Selecciona un país primero" : `Ej: ${getPhonePlaceholder(values.country)}`}
                    style={!values.country ? { cursor: "not-allowed" } : {}}
                  />
                </div>
                {errors.phone && (
                  <p className={styles.invalidFeedback}>{errors.phone}</p>
                )}
              </div>
              {/* Email */}
              <div className={`${styles.formGroup} ${styles.fullWidthField}`}>
                <label htmlFor="email" className={styles.formLabel}>
                  Correo electrónico
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faEnvelope} className={styles.inputIcon} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={values.email}
                    onChange={handleChange}
                    className={`${styles.authFormInput} ${errors.email ? styles.isInvalid : ""}`}
                    placeholder="ejemplo@correo.com"
                  />
                </div>
                {errors.email && (
                  <p className={styles.invalidFeedback}>{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div className={styles.formGroup}>
                <label htmlFor="password" className={styles.formLabel}>
                  Contraseña
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={values.password}
                    onChange={handleChange}
                    maxLength={16}
                    className={`${styles.authFormInput} ${errors.password ? styles.isInvalid : ""
                      }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={styles.inputButton}
                    title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {errors.password && (
                  <p className={styles.invalidFeedback}>{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword" className={styles.formLabel}>
                  Confirmar contraseña
                </label>
                <div className={styles.inputContainer}>
                  <FontAwesomeIcon icon={faLock} className={styles.inputIcon} />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={values.confirmPassword}
                    onChange={handleChange}
                    className={`${styles.authFormInput} ${errors.confirmPassword ? styles.isInvalid : ""
                      }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={styles.inputButton}
                    title={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    <FontAwesomeIcon
                      icon={showConfirmPassword ? faEyeSlash : faEye}
                    />
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className={styles.invalidFeedback}>{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <button type="submit" disabled={loading || !isValid} className={`${btnStyles.primary} ${btnStyles.fullWidth}`} style={{ marginTop: '16px' }}>
              {loading ? (
                <div className={styles.loadingContent}>
                  <div className={styles.loadingSpinner}></div>
                  Creando cuenta...
                </div>
              ) : (
                <>
                  Crear Cuenta
                  <span className={styles.buttonArrow}>→</span>
                </>
              )}
            </button>
          </form>

          <div className={styles.divider}>
            <div className={styles.dividerLine}></div>
            <div className={styles.dividerText}>o</div>
            <div className={styles.dividerLine}></div>
          </div>

          <button
            onClick={handleGoogleRegister}
            disabled={loading || !values.country}
            className={styles.googleButton}
          >
            <FontAwesomeIcon icon={faGoogle} className={styles.googleIcon} />
            Registrarse con Google
          </button>

          <div className={styles.authLinks}>
            <p className={styles.authSubtitle}>
              ¿Ya tienes cuenta?{" "}
              <Link
                to="/login"
                className={styles.authLink}
                style={{ fontWeight: '600' }}
              >
                Inicia sesión
              </Link>
            </p>
          </div>

          <div className={styles.authFooter}>
            <Link to="/" className={styles.backLink}>
              ← Volver al inicio
            </Link>
            <div className={styles.copyrightText}>
              © 2026 {theme.brand.name}
            </div>
          </div>
        </div>

        <div className={styles.authSideMessage}>
          <div className={styles.sideMessageContent}>
            <div className={styles.messageBadge}>
              Únete a Nosotros
            </div>
            <h2>Comienza a enviar hoy mismo</h2>
            <p>
              Forma parte de la red de envíos más eficiente y confiable. Un solo registro te separa de una experiencia logística sin complicaciones hacia Venezuela.
            </p>
            <div style={{ marginTop: '20px', opacity: 0.8, fontSize: '14px' }}>
              <FontAwesomeIcon icon={faTruckFast} style={{ marginRight: '10px' }} />
              Tu carga, nuestra prioridad.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
